// Unified controller for the bookings page.
//
// One page, state-driven: it renders the section that matches the client's
// current state (signed out · ask · request status · purchase · schedule ·
// sessions) instead of stepping through a wizard. Nothing is hidden inside a
// section.
import { get, post } from './api.js';
import { getSession } from './auth.js';
import { initCalendar } from './calendar.js';

function esc(v) {
  return String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

function defaultGuideSlug() {
  try { return JSON.parse(document.body.dataset.brand || '{}').guide.slug || ''; } catch (e) { return ''; }
}

function init() {
  const root = document.getElementById('booking');
  if (!root || root._ready) return;
  root._ready = true;

  const locale = root.dataset.locale || 'en';
  const s = JSON.parse(root.dataset.i18n || '{}');            // pages.bookings
  const r = JSON.parse(root.dataset.i18nRequests || '{}');    // pages.requests
  const services = JSON.parse(root.dataset.services || '[]');
  const signinUrl = root.dataset.signinUrl || '#';
  const signupUrl = root.dataset.signupUrl || '#';

  const gate = document.getElementById('request-gate');
  const flow = document.getElementById('booking-flow');
  const sched = document.getElementById('schedule-widget');
  const schedS = JSON.parse((sched && sched.dataset.i18n) || '{}'); // pages.schedule
  const sessions = document.getElementById('booking-sessions');
  const sessionsList = document.getElementById('booking-sessions-list');
  const statusLive = document.getElementById('booking-status');

  const BOOKING_ERRORS = {
    email_not_confirmed: { en: 'Please confirm your email address before booking.', ru: 'Пожалуйста, подтвердите email перед записью.', he: 'אנא אשרו את האימייל לפני ההזמנה.' },
    age_not_confirmed: { en: 'You must confirm you are 18 or older to book a session.', ru: 'Необходимо подтвердить, что вам 18 лет или больше.', he: 'יש לאשר שאתם בני 18 ומעלה.' }
  };
  const be = (code) => (BOOKING_ERRORS[code] || {})[locale] || (BOOKING_ERRORS[code] || {}).en || code;

  const st = { user: null, req: null, messages: [], transfer: null, guides: [], packages: [], bookings: [] };
  let pickedGuideId = null;
  let pickedService = null;
  let purchaseReady = false;

  const tz = () => localStorage.getItem('site-tz') || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fill = (str, map) => String(str == null ? '' : str).replace(/\{(\w+)\}/g, (m, k) => (k in map ? String(map[k]) : m));
  const show = (node, yes) => { if (node) node.hidden = !yes; };
  const announce = (msg) => { if (statusLive && msg) statusLive.textContent = msg; };
  const hideAll = () => { show(gate, false); show(flow, false); show(sched, false); show(sessions, false); };

  function guideLabel() {
    const chosen = st.guides.find((g) => String(g.id) === String(pickedGuideId));
    if (chosen) return chosen.name;
    if (st.req && st.req.guide_name) return st.req.guide_name;
    if (st.guides.length === 1) return st.guides[0].name;
    return r.guideFallback || 'Your guide';
  }

  function activePackage() {
    return st.packages.find((p) => p.status === 'active' && p.sessions_remaining > 0) || null;
  }

  // ── Gate ────────────────────────────────────────────────────────────────
  function guidePickerHtml() {
    if (st.guides.length <= 1) return '';
    return '<fieldset class="flow-form__fieldset request-gate__guides"><legend>' + esc(r.guidePickLabel || '') + '</legend>' +
      st.guides.map((g) => '<label class="flow-form__radio"><input type="radio" name="guide" value="' + g.id + '"' +
        (String(g.id) === String(pickedGuideId) ? ' checked' : '') + '> ' + esc(g.name) + '</label>').join('') +
      '</fieldset>';
  }

  function intakeFormHtml() {
    return '<h2 class="request-gate__title">' + esc(r.title || '') + '</h2>' +
      '<p class="request-gate__body">' + esc(fill(r.intro, { guide: guideLabel() })) + '</p>' +
      '<form class="request-gate__form" data-intake>' +
        guidePickerHtml() +
        '<div class="flow-form__field"><label for="rq-story">' + esc(r.storyLabel || '') + '</label>' +
          '<textarea id="rq-story" name="story" rows="3" placeholder="' + esc(r.storyPlaceholder || '') + '"></textarea></div>' +
        '<fieldset class="flow-form__fieldset"><legend>' + esc(s.experienceLabel || '') + '</legend>' +
          '<label class="flow-form__radio"><input type="radio" name="experience" value="yes"> ' + esc(s.experienceYes || '') + '</label>' +
          '<label class="flow-form__radio"><input type="radio" name="experience" value="no"> ' + esc(s.experienceNo || '') + '</label>' +
          '<label class="flow-form__radio"><input type="radio" name="experience" value="some"> ' + esc(s.experienceSome || '') + '</label>' +
        '</fieldset>' +
        '<div class="booking-flow__consents"><label class="flow-form__checkbox">' +
          '<input type="checkbox" name="is_adult" required><span>' + esc(s.ageLabel || '') + '</span></label></div>' +
        '<button class="button" type="submit">' + esc(r.submit || '') + '</button>' +
      '</form>';
  }

  function messagesHtml(messages) {
    if (!messages || !messages.length) return '';
    return '<ul class="request-gate__thread">' + messages.map((m) =>
      '<li class="request-gate__bubble request-gate__bubble--' + esc(m.author) + '">' + esc(m.body) + '</li>').join('') + '</ul>';
  }

  function replyFormHtml() {
    return '<form class="request-gate__reply" data-reply>' +
      '<div class="flow-form__field"><label for="rq-reply">' + esc(r.replyLabel || '') + '</label>' +
        '<textarea id="rq-reply" name="body" rows="3" placeholder="' + esc(r.replyPlaceholder || '') + '"></textarea></div>' +
      '<button class="button" type="submit">' + esc(r.replyCta || '') + '</button>' +
    '</form>';
  }

  function transferHtml(transfer) {
    const body = fill(r.transferBody, { guide: st.req && st.req.guide_name ? st.req.guide_name : (r.guideFallback || 'Your guide'), toGuide: transfer.to_guide_name || '' });
    return '<div class="request-gate__transfer">' +
      '<h3 class="request-gate__transfer-title">' + esc(r.transferTitle || '') + '</h3>' +
      '<p class="request-gate__body">' + esc(body) + '</p>' +
      '<div class="request-gate__transfer-actions">' +
        '<button class="button" type="button" data-transfer-accept>' + esc(r.transferAccept || '') + '</button>' +
        '<button class="button button--secondary" type="button" data-transfer-decline>' + esc(r.transferDecline || '') + '</button>' +
      '</div></div>';
  }

  function renderSignedOut() {
    hideAll();
    show(gate, true);
    gate.innerHTML = '<h2 class="request-gate__title">' + esc(s.authTitle || 'Account required') + '</h2>' +
      '<p class="request-gate__body">' + esc(s.accountNotice || '') + '</p>' +
      '<div class="booking-flow__auth-actions">' +
        '<a class="button" href="' + esc(signinUrl) + '">' + esc(s.authLogin || 'Log in') + '</a>' +
        '<a class="button button--secondary" href="' + esc(signupUrl) + '">' + esc(s.authRegister || 'Create account') + '</a>' +
      '</div>';
  }

  function renderGate() {
    hideAll();
    show(gate, true);
    const req = st.req;
    if (!req) {
      gate.innerHTML = intakeFormHtml();
      wireIntake();
      return;
    }
    if (req.status === 'declined') {
      gate.innerHTML = '<h2 class="request-gate__title">' + esc(r.declinedTitle || '') + '</h2>' +
        '<p class="request-gate__body">' + esc(fill(r.declinedBody, { guide: req.guide_name || (r.guideFallback || 'Your guide') })) + '</p>' +
        messagesHtml(st.messages) + intakeFormHtml();
      wireIntake();
      return;
    }
    const guide = req.guide_name || (r.guideFallback || 'Your guide');
    const title = req.status === 'needs_info' ? (r.needsInfoTitle || '') : (r.pendingTitle || '');
    gate.innerHTML = '<h2 class="request-gate__title">' + esc(title) + '</h2>' +
      (req.status === 'needs_info' ? '' : '<p class="request-gate__body">' + esc(fill(r.pendingBody, { guide: guide })) + '</p>') +
      messagesHtml(st.messages) +
      (st.transfer ? transferHtml(st.transfer) : '') +
      replyFormHtml();
    if (st.transfer) wireTransfer();
    wireReply();
  }

  // ── Purchase ────────────────────────────────────────────────────────────
  function formatPrice(usd) {
    const opts = { style: 'currency', currency: 'USD', currencyDisplay: locale === 'he' ? 'code' : 'narrowSymbol' };
    const region = locale === 'he' ? 'he-IL' : locale === 'ru' ? 'ru-RU' : 'en-US';
    let val;
    try { val = new Intl.NumberFormat(region, opts).format(usd); } catch (e) { return '$' + usd; }
    if (locale === 'he') val = val.replace('USD', '$');
    return val;
  }

  function currentTotal() {
    return pickedService && pickedService.price ? pickedService.price.usd : 0;
  }

  function detailsValid() {
    const name = document.getElementById('book-name');
    const email = document.getElementById('book-email');
    const consent = flow.querySelector('[name="consent"]');
    const adult = flow.querySelector('[name="is_adult"]');
    return !!(pickedService && name && name.value.trim() && email && email.value.includes('@') && consent && consent.checked && adult && adult.checked);
  }

  function refreshPurchase() {
    const priceEl = document.getElementById('summary-price');
    if (priceEl) priceEl.textContent = pickedService ? formatPrice(currentTotal()) : '—';
    const submit = document.getElementById('booking-submit');
    if (submit) submit.disabled = !detailsValid();
  }

  function wirePurchase() {
    if (purchaseReady) return;
    purchaseReady = true;

    flow.addEventListener('change', (e) => {
      const svc = e.target.closest('.booking-flow__service-card input[type="radio"]');
      if (svc) {
        const card = svc.closest('.booking-flow__service-card');
        pickedService = JSON.parse(card.dataset.service);
        refreshPurchase();
        return;
      }
      if (e.target.closest('[name="consent"], [name="is_adult"]')) refreshPurchase();
    });
    flow.addEventListener('input', (e) => {
      if (e.target.closest('#book-name, #book-email')) refreshPurchase();
    });
    flow.addEventListener('click', (e) => {
      if (e.target.closest('#booking-submit')) submitPurchase();
    });

    getSession().then((user) => {
      if (!user) return;
      const name = document.getElementById('book-name');
      const email = document.getElementById('book-email');
      if (name && user.name) name.value = user.name;
      if (email && user.email) email.value = user.email;
      refreshPurchase();
    });
  }

  function submitPurchase() {
    const btn = document.getElementById('booking-submit');
    if (!btn || !pickedService) return;
    if (!btn.dataset.label) btn.dataset.label = btn.textContent;
    btn.disabled = true;
    btn.textContent = flow.dataset.processingLabel || 'Processing…';
    const payload = { service: pickedService.title, locale: locale };
    if (pickedGuideId) payload.guide_id = pickedGuideId;
    post('/packages', payload).then((result) => {
      window.location.href = result.url;
    }).catch((err) => {
      const known = !!(err.code && BOOKING_ERRORS[err.code]);
      if (typeof window.showToast === 'function') {
        window.showToast(known ? be(err.code) : (r.error || 'Something went wrong. Please try again.'), known ? 'info' : 'attention');
      }
      btn.disabled = false;
      btn.textContent = btn.dataset.label || btn.textContent;
    });
  }

  // ── Scheduling ──────────────────────────────────────────────────────────
  let schedState = { pkg: null, picks: [] };

  function schedFill(str, vals) {
    let out = String(str || '');
    Object.keys(vals).forEach((k) => { out = out.replace('{' + k + '}', vals[k]); });
    return out;
  }

  function refreshSched() {
    const count = sched.querySelector('[data-schedule-count]');
    const submit = sched.querySelector('[data-schedule-submit]');
    if (count && schedState.pkg) {
      count.textContent = schedFill(schedS.count, { count: schedState.picks.length, total: schedState.pkg.sessions_remaining });
    }
    if (submit) submit.disabled = schedState.picks.length === 0;
  }

  function renderSchedule() {
    const pkg = activePackage();
    schedState = { pkg: pkg, picks: [] };
    if (!pkg) { show(sched, false); return; }
    show(sched, true);
    const info = sched.querySelector('[data-schedule-info]');
    const form = sched.querySelector('[data-schedule-form]');
    if (info) info.textContent = schedFill(schedS.remaining, { remaining: pkg.sessions_remaining, total: pkg.sessions_total });
    const cal = sched.querySelector('[data-schedule-calendar]');
    if (form) form.hidden = false;
    if (cal && !cal._inited) {
      cal._inited = true;
      initCalendar(cal, {
        locale: locale,
        multi: true,
        max: pkg.sessions_remaining,
        clientTz: tz(),
        guideSlug: pkg.guide_slug || defaultGuideSlug(),
        onSelect: (list) => { schedState.picks = Array.isArray(list) ? list : []; refreshSched(); }
      });
    }
    refreshSched();
  }

  function wireSchedule() {
    const form = sched.querySelector('[data-schedule-form]');
    if (!form || form._wired) return;
    form._wired = true;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const pkg = schedState.pkg;
      if (!pkg || !schedState.picks.length) return;
      const btn = sched.querySelector('[data-schedule-submit]');
      const note = sched.querySelector('[data-schedule-note]');
      if (btn) btn.disabled = true;
      post('/packages/' + pkg.id + '/schedule', { slots: schedState.picks, is_adult: true, client_timezone: tz() }).then(() => {
        window.location.reload();
      }).catch((err) => {
        if (btn) btn.disabled = false;
        if (note) { note.hidden = false; note.textContent = (err && err.message) || schedS.error || 'Something went wrong.'; }
      });
    });
  }

  // ── Sessions ────────────────────────────────────────────────────────────
  function renderSessions() {
    if (!sessionsList) return;
    const rows = st.bookings.filter((b) => b.status !== 'cancelled');
    show(sessions, rows.length > 0);
    sessionsList.innerHTML = rows.length
      ? '<ul class="booking-sessions__list">' + rows.map((b) =>
          '<li class="booking-session" data-booking-id="' + b.id + '">' +
            '<span class="booking-session__when">' + esc(b.booking_date) + ' · ' + esc(b.booking_time) + '</span>' +
            '<span class="booking-session__service">' + esc(b.service) + '</span>' +
            '<span class="booking-session__actions">' +
              (b.status === 'pending' ? '<button class="button button--small button--secondary" type="button" data-reschedule="' + b.id + '" data-guide-slug="' + esc(b.guide_slug || '') + '">' + esc(s.reschedule || '') + '</button>' : '') +
              '<button class="button button--small button--secondary" type="button" data-cancel="' + b.id + '">' + esc(s.cancel || '') + '</button>' +
            '</span>' +
            '<div class="booking-session__reschedule" data-reschedule-slot="' + b.id + '" hidden></div>' +
          '</li>').join('') + '</ul>'
      : '<p class="booking-sessions__empty">' + esc(s.sessionsEmpty || '') + '</p>';
  }

  function wireSessions() {
    if (!sessionsList || sessionsList._wired) return;
    sessionsList._wired = true;
    sessionsList.addEventListener('click', (e) => {
      const cancel = e.target.closest('[data-cancel]');
      if (cancel) {
        if (!window.confirm(s.cancelConfirm || 'Cancel this session?')) return;
        post('/bookings/' + cancel.dataset.cancel + '/cancel').then(() => load()).catch(() => {});
        return;
      }
      const resched = e.target.closest('[data-reschedule]');
      if (resched) openReschedule(resched.dataset.reschedule, resched.dataset.guideSlug, resched);
    });
  }

  function openReschedule(id, guideSlug, btn) {
    const slot = sessionsList.querySelector('[data-reschedule-slot="' + id + '"]');
    if (!slot) return;
    if (!slot.hidden) { slot.hidden = true; slot.innerHTML = ''; return; }
    slot.hidden = false;
    slot.innerHTML = '<p class="booking-session__reschedule-title">' + esc(s.rescheduleTitle || '') + '</p>' +
      '<div class="calendar" data-min-advance-days="1" data-reschedule-calendar></div>';
    const host = slot.querySelector('[data-reschedule-calendar]');
    initCalendar(host, {
      locale: locale,
      clientTz: tz(),
      guideSlug: guideSlug || defaultGuideSlug(),
      onSelect: (sel) => {
        if (!sel) return;
        post('/bookings/' + id + '/reschedule', { new_date: sel.date, new_time: sel.time }).then(() => load()).catch(() => {});
      }
    });
    if (btn) btn.disabled = true;
  }

  // ── Wiring the gate actions ─────────────────────────────────────────────
  function wireIntake() {
    const form = gate.querySelector('[data-intake]');
    if (!form) return;
    form.addEventListener('change', (e) => {
      const g = e.target.closest('[name="guide"]');
      if (g) { pickedGuideId = Number(g.value); }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      const adult = form.querySelector('[name="is_adult"]');
      const exp = form.querySelector('[name="experience"]:checked');
      const guide = form.querySelector('[name="guide"]:checked');
      btn.disabled = true;
      const body = {
        is_adult: !!(adult && adult.checked),
        story: form.querySelector('[name="story"]').value,
        experience: exp ? exp.value : ''
      };
      const gid = guide ? Number(guide.value) : pickedGuideId;
      if (gid) body.guide_id = gid;
      post('/requests', body).then(() => load()).catch(() => {
        btn.disabled = false;
        alert(r.error || 'Something went wrong.');
      });
    });
  }

  function wireReply() {
    const form = gate.querySelector('[data-reply]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      const body = form.querySelector('[name="body"]').value;
      if (!body.trim()) return;
      btn.disabled = true;
      post('/me/requests/messages', { body: body }).then(() => load()).catch(() => { btn.disabled = false; });
    });
  }

  function wireTransfer() {
    const block = gate.querySelector('.request-gate__transfer');
    if (!block) return;
    const respond = (accept) => post('/me/requests/transfer', { accept: accept }).then(() => load()).catch(() => {});
    const yes = block.querySelector('[data-transfer-accept]');
    const no = block.querySelector('[data-transfer-decline]');
    if (yes) yes.addEventListener('click', () => respond(true));
    if (no) no.addEventListener('click', () => respond(false));
  }

  // ── Load / render ───────────────────────────────────────────────────────
  function render() {
    const approved = st.req && st.req.status === 'approved';
    if (!approved) {
      renderGate();
      show(sessions, false);
      show(sched, false);
      return;
    }
    show(gate, false);
    show(flow, true);
    wirePurchase();
    wireSchedule();
    renderSchedule();
    wireSessions();
    renderSessions();
  }

  function load() {
    return getSession().then((user) => {
      st.user = user;
      if (!user) { renderSignedOut(); return; }
      return Promise.all([
        get('/me/requests').catch(() => ({ request: null, messages: [], transfer: null })),
        get('/me/packages').catch(() => ({ packages: [] })),
        get('/me/bookings').catch(() => ({ bookings: [] })),
        get('/guides').catch(() => ({ guides: [] }))
      ]).then(([reqData, pkgData, bkData, guideData]) => {
        st.req = (reqData && reqData.request) || null;
        st.messages = (reqData && reqData.messages) || [];
        st.transfer = (reqData && reqData.transfer) || null;
        st.packages = (pkgData && pkgData.packages) || [];
        st.bookings = (bkData && bkData.bookings) || [];
        st.guides = (guideData && guideData.guides) || [];
        if (pickedGuideId == null) {
          pickedGuideId = (st.req && st.req.guide_id) || (st.guides[0] && st.guides[0].id) || null;
        }
        render();
      });
    });
  }

  load().catch(() => { /* unknown state — leave the page as-is */ });
}

document.addEventListener('page:load', init);
